import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatDate, relativeTime, statusClass, kindStyle, priorityStyle, KPI_STRIPE } from "@/lib/format";
import { Users, Receipt, FolderKanban, Bot, Radio, Activity, Gavel, Milestone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PRIORITY_ORDER = { P0: 0, P1: 1, P2: 2 };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/kpi"),
      api.get("/dashboard/feed"),
      api.get("/governance/loops"),
      api.get("/agents"),
      api.get("/clients"),
    ])
      .then(([kpi, feed, loops, agents, clients]) => {
        setData({
          kpi: kpi.data,
          feed: feed.data,
          loops: loops.data.items,
          agents: agents.data.items,
          clients: clients.data.items,
        });
      })
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) {
    return <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">{err}</div>;
  }
  if (!data) {
    return <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>;
  }

  const focusLoops = [...data.loops]
    .filter((l) => l.status !== "CLOSED")
    .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))
    .slice(0, 4);

  const nextMilestones = data.clients
    .flatMap((c) => c.projects.map((p) => ({ ...p, client: c.name, clientId: c.id })))
    .filter((p) => p.status !== "DONE" && p.milestones_done < p.milestones_total)
    .slice(0, 4);

  const events = data.feed.items.filter((i) => i.kind !== "decision").slice(0, 6);
  const decisions = data.feed.items.filter((i) => i.kind === "decision").slice(0, 5);

  return (
    <div data-testid={TID.dashboard} className="space-y-4">
      {/* Row 1 · KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard testId={TID.kpiClients} label="Clienti attivi" value={data.kpi.clients_active} icon={Users} to="/app/clients" hint="in gestione" stripe={KPI_STRIPE.clients} />
        <KpiCard testId={TID.kpiQuotes} label="Preventivi aperti" value={data.kpi.quotes_open} icon={Receipt} to="/app/quotes" hint="da chiudere" stripe={KPI_STRIPE.quotes} />
        <KpiCard testId={TID.kpiProjects} label="Progetti attivi" value={data.kpi.projects_active} icon={FolderKanban} to="/app/clients" hint="in delivery" stripe={KPI_STRIPE.projects} />
        <KpiCard testId={TID.kpiAgents} label="Agenti attivi" value={`${data.kpi.agents_up}/${data.kpi.agents_total}`} icon={Bot} to="/app/agents" hint={data.kpi.agents_up === data.kpi.agents_total ? "tutti UP" : "attenzione"} warn={data.kpi.agents_up !== data.kpi.agents_total} stripe={data.kpi.agents_up === data.kpi.agents_total ? KPI_STRIPE.agents_ok : KPI_STRIPE.agents_warn} />
      </div>

      {/* Row 2 · Operational widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <FocusPanel loops={focusLoops} />
        <AgentHealthPanel agents={data.agents} />
        <MilestonesPanel items={nextMilestones} />
      </div>

      {/* Row 3 · Feed split */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <EventsPanel events={events} />
        <DecisionsPanel decisions={decisions} />
      </div>

      <div className="font-mono text-[10px] text-neutral-600 pt-2">
        generated at · {formatDate(data.feed.generated_at, { withTime: true })}
      </div>
    </div>
  );
}

function KpiCard({ testId, label, value, icon: Icon, to, warn, hint, stripe }) {
  const border = warn ? "border-[#EF4444]/60" : "border-[#1F1F1F]";
  const iconColor = warn ? "text-[#EF4444]" : "text-neutral-500";
  const hintColor = warn ? "text-[#EF4444]" : "text-neutral-500";
  const inner = (
    <div
      data-testid={testId}
      className={`kpi-stripe border ${border} bg-[#0C0C0C] hover:bg-[#141414] transition-colors px-4 py-4 pt-5 flex flex-col gap-2`}
      style={{ "--stripe": stripe || "#EDEDED" }}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
          {label}
        </span>
        <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={1.5} style={warn ? {} : { color: stripe }} />
      </div>
      <div className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-none">
        {value}
      </div>
      {hint && (
        <div className={`font-mono text-[10px] uppercase tracking-widest ${hintColor}`}>
          {hint}
        </div>
      )}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function PanelShell({ testId, title, icon: Icon, count, children, to }) {
  return (
    <section data-testid={testId} className="border border-[#1F1F1F] bg-[#0C0C0C] flex flex-col">
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center justify-between bg-[#141414]">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-3.5 h-3.5 text-neutral-500 shrink-0" strokeWidth={1.5} />
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400 truncate">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {typeof count === "number" && (
            <span className="font-mono text-[10px] text-neutral-600">{count}</span>
          )}
          {to && (
            <Link
              to={to}
              className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#EDEDED] inline-flex items-center gap-1"
            >
              apri
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </header>
      <div className="flex-1 flex flex-col">{children}</div>
    </section>
  );
}

function FocusPanel({ loops }) {
  return (
    <PanelShell testId={TID.focusPanel} title="Focus del giorno · loop" icon={Radio} count={loops.length} to="/app/governance">
      {loops.length === 0 ? (
        <div className="p-4 font-mono text-xs text-neutral-500">Tutti i loop chiusi. Bravo.</div>
      ) : (
        <ul className="divide-y divide-[#1F1F1F]">
          {loops.map((l) => (
            <li key={l.id} className="px-4 py-3 flex items-start gap-3 hover:bg-[#141414]">
              <div className="w-14 shrink-0">
                <div className="font-mono text-[11px] text-neutral-300">{l.id}</div>
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1 py-[1px] inline-block mt-0.5 ${priorityStyle(l.priority)}`}>
                  {l.priority}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#EDEDED] leading-snug line-clamp-2">{l.title}</div>
                <div className="font-mono text-[10px] text-neutral-500 mt-1">
                  {relativeTime(l.opened_at)} · owner {l.owner}
                </div>
              </div>
              <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${statusClass(l.status)}`}>
                {l.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </PanelShell>
  );
}

function AgentHealthPanel({ agents }) {
  const up = agents.filter((a) => a.status === "UP").length;
  return (
    <PanelShell testId={TID.agentHealthPanel} title={`Salute agenti · ${up}/${agents.length} UP`} icon={Bot} to="/app/agents">
      <ul className="divide-y divide-[#1F1F1F]">
        {agents.map((a) => {
          const dot =
            a.status === "UP" ? "bg-[#4ADE80]" :
            a.status === "DEGRADED" ? "bg-[#F5A623]" : "bg-red-400";
          return (
            <li key={a.id} className="px-4 py-3 flex items-center gap-3 hover:bg-[#141414]">
              <span className={`w-2 h-2 rounded-full ${dot} shrink-0`}></span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#EDEDED] truncate">{a.name}</div>
                <div className="font-mono text-[10px] text-neutral-500 truncate">
                  {a.tech} · {relativeTime(a.last_heartbeat)}
                </div>
              </div>
              <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${statusClass(a.status)}`}>
                {a.status}
              </span>
            </li>
          );
        })}
      </ul>
    </PanelShell>
  );
}

function MilestonesPanel({ items }) {
  return (
    <PanelShell testId={TID.milestonesPanel} title="Prossime milestone" icon={Milestone} count={items.length} to="/app/clients">
      {items.length === 0 ? (
        <div className="p-4 font-mono text-xs text-neutral-500">Nessuna milestone attiva.</div>
      ) : (
        <ul className="divide-y divide-[#1F1F1F]">
          {items.map((p) => {
            const pct = Math.round((p.milestones_done / p.milestones_total) * 100);
            return (
              <li key={p.id} className="px-4 py-3 hover:bg-[#141414]">
                <Link to={`/app/clients/${p.clientId}`} className="block">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-neutral-500">{p.client}</span>
                    <span className="font-mono text-[10px] text-neutral-500">{p.milestones_done}/{p.milestones_total}</span>
                  </div>
                  <div className="text-sm text-[#EDEDED] truncate mt-0.5">{p.milestone_current}</div>
                  <div className="mt-2 h-1 bg-[#1F1F1F]">
                    <div className="h-full bg-[#EDEDED]" style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </PanelShell>
  );
}

function EventsPanel({ events }) {
  return (
    <PanelShell testId={TID.eventsPanel} title="Ultimi eventi" icon={Activity} count={events.length}>
      <ul className="divide-y divide-[#1F1F1F]">
        {events.map((e, i) => {
          const k = kindStyle(e.kind);
          return (
            <li key={i} data-testid={TID.feedItem} className="px-4 py-3 hover:bg-[#141414]">
              <div className="flex items-center justify-between gap-3">
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${k.className}`}>
                  {k.label}
                </span>
                <span className="font-mono text-[10px] text-neutral-500 shrink-0" title={e.at}>
                  {relativeTime(e.at)}
                </span>
              </div>
              <div className="font-mono text-xs text-neutral-300 mt-2 leading-relaxed">{e.payload}</div>
            </li>
          );
        })}
      </ul>
    </PanelShell>
  );
}

function DecisionsPanel({ decisions }) {
  return (
    <PanelShell testId={TID.decisionsPanel} title="Ultime decisioni" icon={Gavel} count={decisions.length} to="/app/governance">
      <ul className="divide-y divide-[#1F1F1F]">
        {decisions.map((d, i) => (
          <li key={i} data-testid={TID.feedItem} className="px-4 py-3 hover:bg-[#141414]">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] text-neutral-300">{d.id}</span>
              <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusClass(d.outcome)}`}>
                {d.outcome}
              </span>
            </div>
            <div className="text-sm text-[#EDEDED] mt-1 leading-snug">{d.title}</div>
            <div className="font-mono text-[10px] text-neutral-500 mt-1">
              {relativeTime(d.at)}
            </div>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}
